/**
 * Tabs DOM Component
 *
 * Creates and manages the tab bar — a container holding all tab buttons.
 * Uses event delegation on the container for click handling.
 * No Redux, no business logic.
 *
 * @module nativeInteractiveSiteMap/components/tabs.dom
 */

import createTabDom from './tab.dom';

/**
 * @param {Array<{ label: string, count: (number|null), tabValue: string, isActive: boolean }>} tabConfigs
 * @returns {{ getElement, attachEventListeners, setActiveTab, updateCounts, hide, show, remove }}
 */
export default function createTabsDom(tabConfigs) {
  const state = {
    root: null,
    tabs: new Map(), 
    clickHandler: null,
  };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.setAttribute('role', 'tablist');

    const tabListClass = [
      'nhs-c-native-interactive-site-map__tabs',
      'nhs-mq-native-interactive-site-map__tabs-medium',
      'nhs-mq-native-interactive-site-map__tabs-large',
    ]

    state.root.className = tabListClass.join(' ');

    tabConfigs.forEach((config) => {
      const tab = createTabDom(config);
      state.tabs.set(config.tabValue, tab);
      state.root.appendChild(tab.getElement());
    });
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    /**
     * @param {object} handlers
     * @param {Function} handlers.onTabClick - Called with { tabValue } on tab click
     */
    attachEventListeners(handlers) {
      if (typeof handlers.onTabClick === 'function') {
        state.clickHandler = (event) => {
          const button = event.target.closest('[data-tab-value]');
          if (button && state.root.contains(button)) {
            handlers.onTabClick({ tabValue: button.dataset.tabValue });
          }
        };
        state.root.addEventListener('click', state.clickHandler);
      }
    },

    /**
     * Marks one tab active and all others inactive.
     * @param {string} tabValue
     */
    setActiveTab(tabValue) {
      state.tabs.forEach((tab, value) => {
        tab.setActive(value === tabValue);
      });
    },

    /**
     * Updates count text for each tab whose tabValue appears in the map.
     * @param {Record<string, number>} counts - e.g. { floorPlans: 5, quickMoveIns: 3 }
     */
    updateCounts(counts) {
      Object.entries(counts).forEach(([tabValue, count]) => {
        state.tabs.get(tabValue)?.setCount(count);
      });
    },

    /**
     * Sets the disabled state of a single tab.
     * @param {string} tabValue
     * @param {boolean} disabled
     */
    setTabDisabled(tabValue, disabled) {
      state.tabs.get(tabValue)?.setDisabled(disabled);
    },

    hide() {
      state.root.hidden = true;
    },

    show() {
      state.root.hidden = false;
    },

    remove() {
      if (state.clickHandler) {
        state.root?.removeEventListener('click', state.clickHandler);
        state.clickHandler = null;
      }
      state.tabs.forEach((tab) => tab.remove());
      state.tabs.clear();
      state.root?.remove();
      state.root = null;
    },
  };
}
